import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ClockCommentModal } from "@/pages/employees/time-tracking/components/ClockCommentModal.tsx";
import {
  useClockIn,
  useClockOut,
  useTimeTrackingStatus,
} from "@/queries/use-time-tracking.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

function formatElapsed(clockedInAtMs: number): { h: string; m: string } {
  const diff = Math.max(0, Date.now() - clockedInAtMs);
  const totalMinutes = Math.floor(diff / 60000);
  return {
    h: String(Math.floor(totalMinutes / 60)).padStart(2, "0"),
    m: String(totalMinutes % 60).padStart(2, "0"),
  };
}

function getElapsed(isClockedIn: boolean, clockedInAtMs: number | undefined) {
  return isClockedIn && clockedInAtMs ? formatElapsed(clockedInAtMs) : null;
}

export const ClockInOutButton = () => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { isFeatureEnabled, canIEdit } = usePermissions();

  const hasAccess =
    isFeatureEnabled(ApiFeatureType.clocking) && canIEdit(ApiPermission.clocking_module);

  const { data: status, isLoading } = useTimeTrackingStatus(hasAccess);
  const clockIn = useClockIn();
  const clockOut = useClockOut();

  const [isHovered, setIsHovered] = useState(false);

  const isClockedIn = status?.is_clocked_in ?? false;
  // Axios interceptor deserializes datetime_* fields to Date objects at runtime
  const clockedInAtMs = (
    status?.current_entry?.datetime_clocked_in as unknown as Date | undefined
  )?.getTime();

  const [elapsed, setElapsed] = useState(() => getElapsed(isClockedIn, clockedInAtMs));
  const [syncedClock, setSyncedClock] = useState({ isClockedIn, clockedInAtMs });

  if (
    syncedClock.isClockedIn !== isClockedIn ||
    !Object.is(syncedClock.clockedInAtMs, clockedInAtMs)
  ) {
    setSyncedClock({ isClockedIn, clockedInAtMs });
    setElapsed(getElapsed(isClockedIn, clockedInAtMs));
  }

  useEffect(() => {
    if (!isClockedIn || !clockedInAtMs) return;

    const id = setInterval(() => setElapsed(formatElapsed(clockedInAtMs)), 30000);
    return () => clearInterval(id);
  }, [isClockedIn, clockedInAtMs]);

  if (!hasAccess) return null;

  const isPending = isLoading || clockIn.isPending || clockOut.isPending;

  const openClockInModal = () =>
    openModal(ClockCommentModal, {
      mode: "in",
      onConfirm: async (comment) => {
        const result = await clockIn.mutateAsync(comment);
        analyticsService.trackEvent(analyticEvents.timeTracking.clockedIn);
        return result;
      },
    });

  const openClockOutModal = () =>
    openModal(ClockCommentModal, {
      mode: "out",
      onConfirm: async (comment) => {
        const result = await clockOut.mutateAsync(comment);
        analyticsService.trackEvent(analyticEvents.timeTracking.clockedOut);
        return result;
      },
    });

  if (isClockedIn) {
    return (
      <Tooltip open={isHovered} delay={false}>
        <TooltipTrigger>
          <Button
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={openClockOutModal}
            variant={"secondary"}
            isLoading={isPending}
            leftIcon={
              <FontAwesomeIcon
                icon={isHovered ? "fa-light fa-stop" : "fa-solid fa-play"}
                className={
                  isHovered
                    ? "text-default"
                    : `text-positive ${!isPending ? "animate-clock-pulse" : ""}`
                }
              />
            }
          >
            {elapsed && (
              <>
                {t("clocking:clocked_in_prefix")}: <span className="tabular-nums">{elapsed.h}</span>
                <span className="animate-colon-blink tabular-nums">:</span>
                <span className="tabular-nums">{elapsed.m}</span>
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("clocking:click_to_clock_out")}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu
      trigger={
        <Button
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          isLoading={isPending}
          leftIcon={
            <FontAwesomeIcon icon={isHovered ? "fa-light fa-play" : "fa-light fa-stopwatch"} />
          }
        >
          {t("clocking:clock_in")}
        </Button>
      }
    >
      <DropdownMenuItem
        leftIcon={<FontAwesomeIcon icon="fa-light fa-play" />}
        title={t("clocking:clock_in_now")}
        onClick={() =>
          clockIn.mutate(null, {
            onSuccess: () => analyticsService.trackEvent(analyticEvents.timeTracking.clockedIn),
          })
        }
      />
      <DropdownMenuItem
        leftIcon={<FontAwesomeIcon icon="fa-light fa-comment" />}
        title={t("clocking:clock_in_with_comment")}
        onClick={openClockInModal}
      />
    </DropdownMenu>
  );
};
